/**
 * TSUKIE WhatsApp Widget — storefront integration.
 *
 * Two independent jobs in one file:
 *   1. Background cart-activity tracking (silent — no UI): reports cart
 *      updates and checkout-started events to the backend so abandoned-cart
 *      reminders can fire.
 *   2. Dismissing the optional greeting bubble next to the icon. The icon
 *      itself is a plain <a href="https://wa.me/...">, so clicking it opens
 *      WhatsApp directly — no panel, no toggle, zero JS required for that.
 *
 * No opt-in form here — placing an order auto-enrolls the customer on the
 * backend (see src/routes/webhooks/shopify.ts). Talks ONLY to the TSUKIE
 * WhatsApp backend (never Meta, never Shopify Admin — no secrets live here,
 * only a public backend URL).
 *
 * Hard rules:
 *   - never throws, never blocks add-to-cart, cart open, or checkout
 *   - every network call has a short timeout and is fire-and-forget
 *   - completely inert (beyond the chat link) if wa_automation_enable is off
 */
(function () {
  'use strict';

  var config = window.TsukieWhatsAppConfig || {};
  var BACKEND_URL = config.backendUrl || '';
  var ENABLED = !!config.enabled && !!BACKEND_URL;

  var REQUEST_TIMEOUT_MS = 4000;

  /** Fire-and-forget POST with a hard timeout. Never throws to the caller. */
  function beacon(path, payload) {
    if (!ENABLED) return;
    try {
      var controller = ('AbortController' in window) ? new AbortController() : null;
      var timeoutId = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS) : null;

      fetch(BACKEND_URL + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined,
        keepalive: true,
      })
        .catch(function () { /* backend unreachable — silently ignore, never surface to the shopper */ })
        .finally(function () { if (timeoutId) clearTimeout(timeoutId); });
    } catch (e) {
      // Never let a malformed payload or missing fetch() break the page.
    }
  }

  function mapLineItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function (item) {
      return {
        productId: String(item.product_id),
        variantId: String(item.variant_id || item.id),
        title: item.product_title || item.title,
        quantity: item.quantity,
        price: String((item.price / 100).toFixed(2)), // Shopify cart item prices are in the shop's smallest currency unit
        url: item.url,
        imageUrl: item.image || undefined,
      };
    });
  }

  function reportCartEvent(cart, sourceType) {
    if (!cart || !cart.token) return;
    beacon('/api/events', {
      type: sourceType || 'cart_updated',
      cartToken: cart.token,
      currency: cart.currency,
      totalPrice: String((cart.total_price / 100).toFixed(2)),
      lineItems: mapLineItems(cart),
      email: config.customerEmail || undefined,
      phone: config.customerPhone || undefined,
      shopifyCustomerId: config.customerId || undefined,
    });
  }

  // Listen for the theme's existing cart update event — dispatched with
  // detail.resource = the full Shopify cart object by BOTH ATC code paths.
  // No changes to any existing file required; this is purely additive.
  document.addEventListener('cart:update', function (e) {
    var cart = e && e.detail && e.detail.resource;
    if (cart) reportCartEvent(cart, 'cart_updated');
  });

  // Best-effort "checkout started" signal: a real user click on the cart's
  // checkout CTA (Razorpay's sidecart owns the actual checkout UI — we only
  // observe the click, we never interfere with it).
  document.addEventListener(
    'click',
    function (e) {
      var target = e.target && e.target.closest ? e.target.closest('[data-tsukie-checkout-cta], .cart__checkout-button, [name="checkout"]') : null;
      if (!target) return;
      var cartToken = (window.Shopify && window.Shopify.cartToken) || (window.TsukieCart && window.TsukieCart.token);
      if (cartToken) beacon('/api/events', { type: 'checkout_started', cartToken: cartToken, lineItems: [] });
    },
    true
  );

  // ---------------------------------------------------------------------
  // Widget UI: bubble dismiss only. The icon itself is a plain
  // <a href="https://wa.me/...">, so clicking it opens WhatsApp directly —
  // no panel, no toggle state, zero JS required for the core action. This
  // just wires up closing the optional greeting bubble next to it.
  // ---------------------------------------------------------------------
  function initWidget() {
    var bubbleClose = document.getElementById('tsukie-wa-bubble-close');
    if (!bubbleClose || bubbleClose.dataset.tsukieWaBound) return;
    bubbleClose.dataset.tsukieWaBound = '1';

    bubbleClose.addEventListener('click', function (e) {
      e.stopPropagation();
      e.preventDefault();
      var bubble = document.getElementById('tsukie-wa-bubble');
      if (bubble) bubble.style.display = 'none';
    });
  }

  function init() {
    initWidget();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-run when Shopify re-renders a section in the Theme Editor (OS 2.0),
  // same pattern already used elsewhere in this theme.
  document.addEventListener('shopify:section:load', init);
})();
